import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Plus, Trash2, Edit, LogOut, 
  AlertCircle, Check, X, RefreshCw, Folder
} from 'lucide-react';
import { RippleButton } from '../components/effects/RippleButton';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminPanel() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    client_name: '',
    folder_id: '',
    folder_name: ''
  });
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      setIsAuthenticated(true);
      fetchClients();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setLoginError('');

    try {
      const response = await fetch(`${API}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      localStorage.setItem('adminToken', data.token);
      setIsAuthenticated(true);
      fetchClients();
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    setClients([]);
  };

  const fetchClients = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API}/api/admin/clients`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to fetch clients');
      }

      setClients(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError('');

    try {
      const url = editingClient 
        ? `${API}/api/admin/clients/${editingClient.id}`
        : `${API}/api/admin/clients`;
      
      const method = editingClient ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to save client');
      }

      setShowAddForm(false);
      setEditingClient(null);
      setFormData({ email: '', client_name: '', folder_id: '', folder_name: '' });
      fetchClients();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;

    try {
      const response = await fetch(`${API}/api/admin/clients/${clientId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to delete client');
      }

      fetchClients();
    } catch (err) {
      setError(err.message);
    }
  };

  const openEditForm = (client) => {
    setEditingClient(client);
    setFormData({
      email: client.email,
      client_name: client.client_name,
      folder_id: client.folder_id,
      folder_name: client.folder_name
    });
    setShowAddForm(true);
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <main className="min-h-screen bg-cream flex items-center justify-center px-6" data-testid="admin-login">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 border border-border/30"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-forest rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-serif text-2xl text-forest mb-2">Admin Panel</h1>
            <p className="text-foreground/60 text-sm">Manage client portal access</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground/70 mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 bg-sand/30 border border-border/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-forest/30"
                data-testid="admin-email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground/70 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 bg-sand/30 border border-border/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-forest/30"
                data-testid="admin-password"
              />
            </div>

            {loginError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                <AlertCircle className="w-4 h-4" />
                {loginError}
              </div>
            )}

            <RippleButton
              type="submit"
              disabled={isLoggingIn}
              className="w-full btn-primary"
              data-testid="admin-login-btn"
            >
              {isLoggingIn ? 'Signing in...' : 'Sign In'}
            </RippleButton>
          </form>
        </motion.div>
      </main>
    );
  }

  // Admin Dashboard
  return (
    <main className="min-h-screen bg-cream" data-testid="admin-panel">
      {/* Header */}
      <header className="bg-forest text-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-serif text-xl">Admin Panel</h1>
            <p className="text-white/70 text-sm">Client Portal Management</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Actions Bar */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-xl text-forest">Registered Clients</h2>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchClients}
              className="p-2 text-foreground/60 hover:text-forest transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <RippleButton
              onClick={() => {
                setEditingClient(null);
                setFormData({ email: '', client_name: '', folder_id: '', folder_name: '' });
                setShowAddForm(true);
              }}
              className="btn-primary flex items-center gap-2"
              data-testid="add-client-btn"
            >
              <Plus className="w-5 h-5" />
              Add Client
            </RippleButton>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* Clients Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-border/30 overflow-hidden">
          {isLoading ? (
            <div className="px-6 py-12 text-center">
              <div className="w-10 h-10 border-4 border-forest/20 border-t-forest rounded-full animate-spin mx-auto mb-4" />
              <p className="text-foreground/60">Loading clients...</p>
            </div>
          ) : clients.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <Users className="w-12 h-12 text-foreground/20 mx-auto mb-4" />
              <p className="text-foreground/60 mb-4">No clients registered yet</p>
              <RippleButton
                onClick={() => setShowAddForm(true)}
                className="btn-primary"
              >
                Add Your First Client
              </RippleButton>
            </div>
          ) : (
            <>
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-sand/30 border-b border-border/30 text-sm font-medium text-foreground/60">
                <div className="col-span-3">Client Name</div>
                <div className="col-span-3">Email</div>
                <div className="col-span-3">Folder</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-1 text-right">Actions</div>
              </div>

              {/* Rows */}
              {clients.map((client) => (
                <div
                  key={client.id}
                  className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-border/20 last:border-0 hover:bg-sand/20 transition-colors items-center"
                >
                  <div className="col-span-3 font-medium text-foreground">{client.client_name}</div>
                  <div className="col-span-3 text-foreground/70 text-sm">{client.email}</div>
                  <div className="col-span-3 flex items-center gap-2 text-sm">
                    <Folder className="w-4 h-4 text-amber-500" />
                    <span className="text-foreground/70 truncate" title={client.folder_name}>
                      {client.folder_name}
                    </span>
                  </div>
                  <div className="col-span-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      client.is_active 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {client.is_active ? (
                        <><Check className="w-3 h-3" /> Active</>
                      ) : (
                        <><X className="w-3 h-3" /> Inactive</>
                      )}
                    </span>
                  </div>
                  <div className="col-span-1 flex justify-end gap-2">
                    <button
                      onClick={() => openEditForm(client)}
                      className="p-2 text-foreground/40 hover:text-forest transition-colors"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(client.id)}
                      className="p-2 text-foreground/40 hover:text-red-500 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Help Text */}
        <div className="mt-8 p-6 bg-sand/30 rounded-2xl border border-border/20">
          <h3 className="font-serif text-lg text-forest mb-3">How to get Zoho Folder ID</h3>
          <ol className="space-y-2 text-sm text-foreground/70">
            <li>1. Open Zoho WorkDrive and navigate to the folder</li>
            <li>2. Look at the URL - it will be like: workdrive.zoho.com/folder/<strong>FOLDER_ID_HERE</strong></li>
            <li>3. Copy that ID and paste it in the "Folder ID" field above</li>
          </ol>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6"
            onClick={() => setShowAddForm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md"
            >
              <h3 className="font-serif text-xl text-forest mb-6">
                {editingClient ? 'Edit Client' : 'Add New Client'}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground/70 mb-2">
                    Client Name *
                  </label>
                  <input
                    type="text"
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    required
                    placeholder="e.g., John Doe"
                    className="w-full px-4 py-3 bg-sand/30 border border-border/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-forest/30"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground/70 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    disabled={!!editingClient}
                    placeholder="client@example.com"
                    className="w-full px-4 py-3 bg-sand/30 border border-border/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-forest/30 disabled:opacity-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground/70 mb-2">
                    Zoho Folder ID *
                  </label>
                  <input
                    type="text"
                    value={formData.folder_id}
                    onChange={(e) => setFormData({ ...formData, folder_id: e.target.value })}
                    required
                    placeholder="e.g., abc123xyz"
                    className="w-full px-4 py-3 bg-sand/30 border border-border/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-forest/30"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground/70 mb-2">
                    Folder Display Name *
                  </label>
                  <input
                    type="text"
                    value={formData.folder_name}
                    onChange={(e) => setFormData({ ...formData, folder_name: e.target.value })}
                    required
                    placeholder="e.g., Project Files - John"
                    className="w-full px-4 py-3 bg-sand/30 border border-border/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-forest/30"
                  />
                </div>

                {formError && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    {formError}
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="flex-1 px-4 py-3 border border-border/30 rounded-xl text-foreground/70 hover:bg-sand/30 transition-colors"
                  >
                    Cancel
                  </button>
                  <RippleButton
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 btn-primary"
                  >
                    {isSubmitting ? 'Saving...' : (editingClient ? 'Update' : 'Add Client')}
                  </RippleButton>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
